import { DefaultOption } from 'src/components/molecules';
import {
  PREVIEW_SCREENS,
  PreviewScreenType,
} from 'src/types/formQuestionnaire';

export type PreviewState = {
  screen: PreviewScreenType;
  currentQuestionIndex: number;
  answers: Record<string, QuestionAnswer>;
};

export type QuestionAnswer = {
  shortText?: string;
  longText?: string;
  numeric?: string;
  radioOption?: string | null;
  otherOptionText?: string;
  checkboxOptions?: string[];
  singleDropdownOption?: DefaultOption | null;
  multiDropdownOptions?: DefaultOption[];
  date?: string;
  time?: string;
  dataGridRows?: Array<Record<string, string>>;
  uploadedFiles?: Array<{
    id: string;
    name: string;
    size: number;
    progress?: number;
    error?: string;
  }>;
  databaseFieldSearchText?: string;
};

export type PreviewAction =
  | { type: 'SET_SCREEN'; payload: PreviewScreenType }
  | { type: 'SET_QUESTION_INDEX'; payload: number }
  | { type: 'SET_SHORT_TEXT'; questionId: string; payload: string }
  | { type: 'SET_LONG_TEXT'; questionId: string; payload: string }
  | { type: 'SET_RADIO'; questionId: string; payload: string | null }
  | { type: 'SET_OTHER_TEXT'; questionId: string; payload: string }
  | { type: 'TOGGLE_CHECKBOX'; questionId: string; payload: string }
  | {
      type: 'SET_SINGLE_DROPDOWN';
      questionId: string;
      payload: DefaultOption | null;
    }
  | { type: 'SET_MULTI_DROPDOWN'; questionId: string; payload: DefaultOption[] }
  | {
      type: 'UPDATE_GRID_CELL';
      questionId: string;
      rowIndex: number;
      columnId: string;
      value: string;
    }
  | { type: 'ADD_GRID_ROW'; questionId: string }
  | {
      type: 'ADD_FILES';
      questionId: string;
      payload: Array<{ id: string; name: string; size: number }>;
    }
  | { type: 'DELETE_FILE'; questionId: string; payload: string }
  | {
      type: 'UPDATE_FILE_PROGRESS';
      questionId: string;
      fileId: string;
      progress: number;
    }
  | { type: 'SET_DATE'; questionId: string; payload: string }
  | { type: 'SET_TIME'; questionId: string; payload: string }
  | { type: 'SET_NUMERIC'; questionId: string; payload: string }
  | {
      type: 'SET_DATABASE_FIELD_SEARCH';
      questionId: string;
      payload: string;
    }
  | { type: 'RESET_ALL' };

export const initialPreviewState: PreviewState = {
  screen: PREVIEW_SCREENS.QUESTIONS,
  currentQuestionIndex: 0,
  answers: {},
};

const updateAnswer = (
  state: PreviewState,
  questionId: string,
  update: Partial<QuestionAnswer>,
): PreviewState => ({
  ...state,
  answers: {
    ...state.answers,
    [questionId]: {
      ...state.answers[questionId],
      ...update,
    },
  },
});

const ANSWER_ACTION_KEY: Record<string, keyof QuestionAnswer> = {
  SET_SHORT_TEXT: 'shortText',
  SET_LONG_TEXT: 'longText',
  SET_RADIO: 'radioOption',
  SET_OTHER_TEXT: 'otherOptionText',
  SET_SINGLE_DROPDOWN: 'singleDropdownOption',
  SET_MULTI_DROPDOWN: 'multiDropdownOptions',
  SET_DATE: 'date',
  SET_TIME: 'time',
  SET_NUMERIC: 'numeric',
};

export const previewReducer = (
  state: PreviewState,
  action: PreviewAction,
): PreviewState => {
  const answerKey = ANSWER_ACTION_KEY[action.type];
  if (answerKey && 'questionId' in action && 'payload' in action) {
    return updateAnswer(state, action.questionId, {
      [answerKey]: action.payload,
    });
  }

  switch (action.type) {
    case 'SET_SCREEN':
      return { ...state, screen: action.payload };

    case 'SET_QUESTION_INDEX':
      return { ...state, currentQuestionIndex: action.payload };

    case 'TOGGLE_CHECKBOX': {
      const current = state.answers[action.questionId]?.checkboxOptions || [];
      const updated = current.includes(action.payload)
        ? current.filter((id) => id !== action.payload)
        : [...current, action.payload];
      return updateAnswer(state, action.questionId, {
        checkboxOptions: updated,
      });
    }

    case 'UPDATE_GRID_CELL': {
      const rows = [
        ...(state.answers[action.questionId]?.dataGridRows || [{}]),
      ];
      rows[action.rowIndex] = {
        ...rows[action.rowIndex],
        [action.columnId]: action.value,
      };
      return updateAnswer(state, action.questionId, { dataGridRows: rows });
    }

    case 'ADD_GRID_ROW': {
      const currentRows = state.answers[action.questionId]?.dataGridRows || [
        {},
      ];
      return updateAnswer(state, action.questionId, {
        dataGridRows: [...currentRows, {}],
      });
    }

    case 'ADD_FILES': {
      const existing = state.answers[action.questionId]?.uploadedFiles || [];
      const newFiles = action.payload.map((f) => ({ ...f, progress: 0 }));
      return updateAnswer(state, action.questionId, {
        uploadedFiles: [...existing, ...newFiles],
      });
    }

    case 'UPDATE_FILE_PROGRESS': {
      const allFiles = state.answers[action.questionId]?.uploadedFiles || [];
      return updateAnswer(state, action.questionId, {
        uploadedFiles: allFiles.map((f) =>
          f.id === action.fileId ? { ...f, progress: action.progress } : f,
        ),
      });
    }

    case 'DELETE_FILE': {
      const files = state.answers[action.questionId]?.uploadedFiles || [];
      return updateAnswer(state, action.questionId, {
        uploadedFiles: files.filter((f) => f.id !== action.payload),
      });
    }

    case 'SET_DATABASE_FIELD_SEARCH':
      return updateAnswer(state, action.questionId, {
        databaseFieldSearchText: action.payload,
      });

    case 'RESET_ALL':
      return initialPreviewState;

    default:
      return state;
  }
};
